import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeSection, fetchFeedPosts, fetchSellerReviews, setModal } from '../../Features/lensSlice';
import Post from './Components/Post';
import { Link, useLocation } from 'react-router-dom';
import Modal from './Components/Modal/Modal';
import Loader from './Components/Loader';
import { getPostProfile } from '../../lib/utils/apiCalls';
import { getCachedMediaUrl, getInitials } from '../../lib/utils/utils';
import ReviewPost from './Components/ReviewPost';

function LensAndReviews() {
    const dispatch = useDispatch();
    const location = useLocation();

    const feedRef = useRef(null);

    const activeSection = useSelector((state) => state.lens.activeSection);
    const feedPosts = useSelector((state) => state.lens.feedPosts.feedPosts);

    const feedPostsLoading = useSelector((state) => state.lens.feedPosts.loading);
    const sellerReviewsLoading = useSelector((state) => state.lens.sellerReviews.loading);

    const storeData = useSelector((state) => state.lens.storeData.storeData);
    const sellerReviews = useSelector((state) => state.lens.sellerReviews.sellerReviews);

    const [userProfile, setUserProfile] = useState({});

    const [buttonVisible, setButtonVisible] = useState(false);

    const handleSelection = (event) => {
        dispatch(changeSection(event.target.id));
    }

    const handleCreateLensButton = () => {
        dispatch(setModal('create'));
    }

    useEffect(() => {
        const getUserProfile = async () => {
            const profile = await getPostProfile(storeData.id);
            setUserProfile(profile);
        }

        setTimeout(() => {
            Object.keys(storeData).length > 0 && getUserProfile();
        }, 200);
    }, [storeData]);

    useEffect(() => {
        setTimeout(() => {
            dispatch(fetchFeedPosts());
            dispatch(fetchSellerReviews());
        }, 200);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const transitionButton = () => {
        if (feedRef.current.scrollTop > 2400) {
            setButtonVisible(true);
        }
        else {
            setButtonVisible(false);
        }
    }

    const backToTop = () => {
        if (buttonVisible && feedRef.current) {
            feedRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    useEffect(() => {
        const refElement = feedRef.current;

        if (refElement) {
            const handleScroll = () => transitionButton(feedRef);
            refElement.addEventListener('scroll', handleScroll);

            return () => {
                refElement.removeEventListener('scroll', handleScroll);
            };
        }
    }, [feedRef, activeSection]);

    // Memoize sorted feed posts
    const sortedFeedPosts = useMemo(() => {
        return [...feedPosts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [feedPosts]);

    // Memoize sorted buyer reviews
    const sortedSellerReviews = useMemo(() => {
        return [...sellerReviews].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [sellerReviews]);

    return (
        <div className="w-full h-[calc(100%-70px)]">
            <div className="flex max-h-full justify-between pt-5 px-5 relative">
                <h1 className="size-fit text-[#121212] font-urbanist-regular font-bold leading-8 text-[24px]">Lens and Reviews</h1>

                <div ref={feedRef} className="pb-4 relative w-[45%] max-h-full overflow-y-scroll flex flex-col no-scrollbar">
                    <div className="sticky z-20 top-0">
                        <div className="bg-[#FFFFFFEF] pb-2 flex flex-col items-center">
                            <div className="flex rounded-md p-1 bg-[#EAEAEA] w-fit">
                                <button 
                                    id='feed' 
                                    onClick={handleSelection} 
                                    className={`w-[141px] select-none font-mulish text-[16px] leading-5 outline-none border-none rounded-[4px] p-[10px] gap-[10px] 
                                        ${activeSection === 'feed' ? 
                                        'bg-[#111827] font-bold text-[#FFFFFF]' : 
                                        'bg-[#EAEAEA] font-semibold text-[#666666]'}
                                    `}
                                >
                                    Feed
                                </button>

                                <button 
                                    id='reviews'
                                    onClick={handleSelection} 
                                    className={`select-none font-mulish text-[16px] leading-5 outline-none border-none rounded-[4px] p-[10px] gap-[10px] 
                                        ${activeSection === 'reviews' ? 
                                        'bg-[#111827] font-bold text-[#FFFFFF]' : 
                                        'bg-[#EAEAEA] font-semibold text-[#666666]'}
                                    `}
                                >
                                    Customer Review
                                </button>
                            </div>
                        </div>
                    </div>

                    {
                        activeSection === 'feed' && 
                        <div className="flex flex-col items-center w-full">
                            {feedPostsLoading === 'loading' && <Loader />}

                            <div className="flex flex-col gap-4 w-full">
                                {
                                    sortedFeedPosts
                                    .map(post => {
                                        return (
                                            <Post key={post.id} post={post} />
                                        )
                                    })
                                }
                            </div>
                        </div>
                    }

                    {
                        activeSection === 'reviews' && 
                        <div className="flex flex-col items-center w-full">
                            {sellerReviewsLoading === 'loading' && <Loader />}

                            <div className="flex flex-col gap-4 w-full">
                                {
                                    sortedSellerReviews
                                    .map(post => (
                                        <ReviewPost key={post.id} post={post} />
                                    ))
                                }
                            </div>
                        </div>
                    }
                </div>

                {
                    buttonVisible && 
                    <button onClick={backToTop} className='absolute z-20 bottom-12 left-[38%] px-4 py-2 bg-primaryColor hover:bg-primaryAccent text-buttonText rounded-xl text-sm font-bold shadow-[0px_0px_12px_0px_#0000003D]'>Back To Top</button>
                }

                <div className="w-[30%] h-fit p-4 flex flex-col items-center gap-4 border rounded-[9px] border-[#EAEAEA]">
                    <div className="w-full flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="size-[46px] rounded-full overflow-hidden">
                                {
                                    userProfile?.ProfilePic ? 
                                    <img className='size-full object-cover' src={getCachedMediaUrl(userProfile.ProfilePic)} alt="" /> : 
                                    <div className='size-full bg-[#EAEAEA] flex items-center justify-center'>
                                        <p className='font-mulish select-none font-bold text-base text-[#666666]'>{getInitials(userProfile.name)}</p>
                                    </div>
                                }
                            </div>

                            <p className='font-bold select-none text-base text-[#121212]'>{userProfile?.name}</p>
                        </div>

                        <Link to={`${location.pathname}/profile/${storeData.id}`} className='font-semibold select-none text-[12px] leading-[26px] text-[#1055EB]'>View Profile</Link>
                    </div>

                    <button onClick={handleCreateLensButton} className='w-[90%] select-none font-semibold text-center text-[12px] leading-6 px-4 py-[6px] bg-primaryColor hover:bg-primaryAccent text-buttonText rounded-md'>Create Lens</button>
                </div>
            </div>

            <Modal />
        </div>
    );
}

export default LensAndReviews;
